import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { FiX, FiUpload } from 'react-icons/fi';
import axios from 'axios';
import { BASE_URL } from '../constants/config';
import { getTokensFromStore, checkIfTokenExpired } from '../utilities/tokens';
import { refreshTokens } from '../actions/usersActions';
import ImageCropper from './ImageCropper';
import '../styles/editPlayerModal.css';

// Полных лет на сегодня: разница годов давала +1 до дня рождения
const fullYearsSince = (birthday) => {
    const born = new Date(birthday);
    if (isNaN(born.getTime())) return null;
    const now = new Date();
    let years = now.getFullYear() - born.getFullYear();
    if (now.getMonth() < born.getMonth()
        || (now.getMonth() === born.getMonth() && now.getDate() < born.getDate())) {
        years -= 1;
    }
    return years;
};

const POSITIONS = [
    { label: 'Нападающий', value: 'Нападающий' },
    { label: 'Полузащитник', value: 'Полузащитник' },
    { label: 'Защитник', value: 'Защитник' },
    { label: 'Вратарь', value: 'Вратарь' },
    { label: 'Универсал', value: 'Универсал' }
];

const WORKING_FOOT = [
    { label: 'Правая', value: 'Правая' },
    { label: 'Левая', value: 'Левая' },
    { label: 'Обе', value: 'Обе' }
];

export default function EditPlayerModal({ 
    isOpen, 
    onClose, 
    onSuccess, 
    player = null, // если null - режим создания, иначе - редактирования
    isCreateMode = false,
    userData = null, // данные пользователя для client_owner
    // Для импорта из Excel: initialData заполняет форму создания готовыми данными,
    // а onDraftSave получает заполненную карточку вместо отправки на сервер —
    // игрок создаётся позже, вместе с остальными
    initialData = null,
    onDraftSave = null,
}) {
    const dispatch = useDispatch();
    const [isSaving, setIsSaving] = useState(false);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [photoFile, setPhotoFile] = useState(null);
    const [showCropper, setShowCropper] = useState(false);
    const [imageToCrop, setImageToCrop] = useState(null);
    
    const [formData, setFormData] = useState({
        family: '',
        name: '',
        father_name: '',
        birthday: '',
        position: 'Нападающий',
        nationality: 'Россия',
        height: '',
        weight: '',
        working_foot: 'Правая',
        image: ''
    });

    useEffect(() => {
        if (player && !isCreateMode) {
            // Режим редактирования - заполняем форму данными игрока
            setFormData({
                family: player.family || '',
                name: player.name || '',
                father_name: player.father_name || '',
                birthday: player.birthday ? player.birthday.split('T')[0] : '',
                position: player.position || 'Нападающий',
                nationality: player.nationality || 'Россия',
                height: player.height || '',
                weight: player.weight || '',
                working_foot: player.working_foot || 'Правая',
                image: player.image || ''
            });
            
            if (player.image) {
                setPhotoPreview(BASE_URL.url + player.image);
            }
        } else if (initialData) {
            // Создание с готовыми данными (импорт из Excel)
            setFormData({
                family: initialData.family || '',
                name: initialData.name || '',
                father_name: initialData.father_name || '',
                birthday: initialData.birthday || '',
                position: initialData.position || 'Нападающий',
                nationality: initialData.nationality || 'Россия',
                height: initialData.height || '',
                weight: initialData.weight || '',
                working_foot: initialData.working_foot || 'Правая',
                image: ''
            });
            setPhotoPreview(initialData.photoPreview || null);
            setPhotoFile(initialData.photoFile || null);
        } else {
            // Режим создания - очищаем форму
            setFormData({
                family: '',
                name: '',
                father_name: '',
                birthday: '',
                position: 'Нападающий',
                nationality: 'Россия',
                height: '',
                weight: '',
                working_foot: 'Правая',
                image: ''
            });
            setPhotoPreview(null);
            setPhotoFile(null);
        }
    }, [player, isCreateMode, isOpen, initialData]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Проверяем размер файла (максимум 10MB)
            if (file.size > 10 * 1024 * 1024) {
                alert('Размер файла слишком большой. Максимальный размер: 10MB');
                return;
            }
            
            // Читаем файл для отображения в cropper
            const reader = new FileReader();
            reader.onloadend = () => {
                setImageToCrop(reader.result);
                setShowCropper(true);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleCropComplete = (croppedImage) => {
        // croppedImage - это data URL обрезанного изображения
        setPhotoPreview(croppedImage);
        
        // Конвертируем data URL в File object
        fetch(croppedImage)
            .then(res => res.blob())
            .then(blob => {
                const file = new File([blob], 'cropped-image.jpg', { type: 'image/jpeg' });
                setPhotoFile(file);
            });
        
        setShowCropper(false);
        setImageToCrop(null);
    };

    const handleCropCancel = () => {
        setShowCropper(false);
        setImageToCrop(null);
    };

    const handleSave = async () => {
        // Валидация
        if (!(formData.family || '').trim() || !(formData.name || '').trim() || !formData.birthday) {
            alert('Заполните обязательные поля: Фамилия, Имя и Дата рождения');
            return;
        }

        // Черновик для импорта: ничего не отправляем, отдаём карточку наверх
        if (onDraftSave) {
            onDraftSave({
                ...formData,
                family: formData.family.trim(),
                name: formData.name.trim(),
                father_name: (formData.father_name || '').trim(),
                photoFile,
                photoPreview,
            });
            onClose();
            return;
        }

        try {
            setIsSaving(true);
            
            const isTokenExpired = checkIfTokenExpired();
            if (isTokenExpired) {
                await dispatch(refreshTokens());
            }
            
            const tokens = getTokensFromStore();

            let imageBase64 = null;

            // Если загружено новое фото, конвертируем в base64
            if (photoFile) {
                // Проверяем размер файла (максимум 5MB)
                if (photoFile.size > 5 * 1024 * 1024) {
                    alert('Размер файла слишком большой. Максимальный размер: 5MB');
                    setIsSaving(false);
                    return;
                }
                
                const reader = new FileReader();
                imageBase64 = await new Promise((resolve, reject) => {
                    reader.onloadend = () => {
                        const base64String = reader.result.split(',')[1];
                        
                        // Проверяем, что base64 строка не пустая и имеет минимальную длину
                        if (!base64String || base64String.length < 100) {
                            reject(new Error('Ошибка при конвертации изображения: полученная строка слишком короткая'));
                            return;
                        }
                        
                        console.log('Image converted to base64, length:', base64String.length);
                        resolve(base64String);
                    };
                    reader.onerror = (error) => {
                        console.error('FileReader error:', error);
                        reject(error);
                    };
                    reader.readAsDataURL(photoFile);
                });
            }

            const requestData = {
                // Пробелы по краям ломали поиск дублей и сортировку
                name: formData.name.trim(),
                family: formData.family.trim(),
                father_name: (formData.father_name || '').trim(),
                position_id: formData.position === 'Нападающий' ? 1 : 
                             formData.position === 'Полузащитник' ? 2 : 
                             formData.position === 'Защитник' ? 3 : 
                             formData.position === 'Вратарь' ? 4 : 5,
                weight: formData.weight ? parseInt(formData.weight) : null,
                height: formData.height ? parseInt(formData.height) : null,
                nationality: formData.nationality || null,
                work_foot: formData.working_foot || 'Правая',
                birthday: formData.birthday || null,
                age: formData.birthday ? fullYearsSince(formData.birthday) : null
            };
            
            // Обработка изображения
            if (imageBase64) {
                // Проверяем корректность base64 строки перед отправкой
                if (imageBase64.length < 100) {
                    throw new Error('Ошибка: изображение повреждено (слишком маленький размер). Попробуйте загрузить другой файл.');
                }
                requestData.image = imageBase64;
            } else if (isCreateMode) {
                // При создании без фото отправляем false
                requestData.image = false;
            } else {
                // При редактировании без нового фото не отправляем поле image
            }

            let response;
            if (isCreateMode) {
                // Создание нового игрока
                // Добавляем client_owner: 0 для супер-админа (role 7), client_id для админа лиги (role 6)
                // Без client_id игрок становился «ничейным» (client_owner = 0) —
                // такого админ лиги потом не видит и не может править
                const clientOwner = (userData?.role === 7) ? 0 : userData?.client_id;
                if (!clientOwner && userData?.role !== 7) {
                    throw new Error('Не удалось определить вашу лигу (client_id). Перезайдите в админку и повторите.');
                }
                
                // Критическая проверка client_owner
                if (clientOwner === undefined || clientOwner === null) {
                    throw new Error('Не удалось определить владельца клиента. Проверьте данные пользователя.');
                }
                
                requestData.client_owner = clientOwner;
                
                // Дополнительная проверка перед отправкой
                if (requestData.image && typeof requestData.image === 'string' && requestData.image.length < 1000) {
                    throw new Error(`КРИТИЧЕСКАЯ ОШИБКА: Попытка отправить слишком маленькое изображение (${requestData.image.length} символов). Отправка отменена для предотвращения повреждения данных.`);
                }
                
                response = await axios.post(
                    BASE_URL.url + '/players/add',
                    requestData,
                    {
                        headers: {
                            "Authorization": "Bearer " + tokens.access_token,
                            'Content-Type': 'application/json',
                        }
                    }
                );
                
                console.log('Server response after creating player:', response.data);
                
                // Сервер может вернуть разные форматы ответа
                let playerId = null;
                
                // Пробуем разные варианты получения ID
                if (response.data) {
                    playerId = response.data.id || 
                               response.data.player_id || 
                               response.data.playerId ||
                               response.data.insertId ||
                               response.data.data?.id ||
                               response.data.data?.player_id;
                }
                
                console.log('Extracted player ID:', playerId);
                
                if (!playerId) {
                    // Если ID не получен, пробуем найти игрока по ФИО
                    console.warn('Player ID not found in response, searching by name...');
                    try {
                        const searchResponse = await axios.post(
                            BASE_URL.url + '/players/search',
                            { family: formData.family, name: formData.name },
                            {
                                headers: {
                                    "Authorization": "Bearer " + tokens.access_token,
                                    'Content-Type': 'application/json',
                                }
                            }
                        );
                        
                        // Находим игрока по полному совпадению ФИО
                        let foundPlayer = null;
                        if (Array.isArray(searchResponse.data)) {
                            foundPlayer = searchResponse.data.find(p => 
                                p.family === formData.family && 
                                p.name === formData.name &&
                                p.father_name === formData.father_name
                            );
                        } else if (searchResponse.data?.data && Array.isArray(searchResponse.data.data)) {
                            foundPlayer = searchResponse.data.data.find(p => 
                                p.family === formData.family && 
                                p.name === formData.name &&
                                p.father_name === formData.father_name
                            );
                        }
                        
                        if (foundPlayer) {
                            playerId = foundPlayer.id || foundPlayer.player_id;
                            console.log('Found player via search, ID:', playerId);
                        }
                    } catch (searchError) {
                        console.error('Error searching for created player:', searchError);
                    }
                }
                
                alert('Игрок успешно создан!');
                
                // После создания игрока запрашиваем его полные данные с сервера
                // чтобы получить правильный путь к изображению (например, /images/players/{id}.jpg)
                let createdPlayer = null;
                
                if (playerId) {
                    try {
                        const playerDataResponse = await axios.post(
                            BASE_URL.url + `/players/${playerId}`,
                            {
                                headers: {
                                    "Authorization": "Bearer " + tokens.access_token,
                                    'Content-Type': 'application/json',
                                }
                            }
                        );
                        
                        console.log('Fetched player data:', playerDataResponse.data);
                        
                        // Сервер может вернуть данные в разных форматах
                        if (playerDataResponse.data) {
                            createdPlayer = playerDataResponse.data.data || playerDataResponse.data;
                        }
                    } catch (fetchError) {
                        console.error('Error fetching player data:', fetchError);
                    }
                }
                
                // Если не удалось получить данные с сервера, формируем объект вручную
                if (!createdPlayer) {
                    createdPlayer = {
                        id: playerId,
                        player_id: playerId,
                        family: formData.family,
                        name: formData.name,
                        father_name: formData.father_name,
                        image: playerId ? `/images/players/${playerId}.jpg` : imageBase64,
                        position: formData.position,
                        position_id: requestData.position_id,
                        weight: requestData.weight,
                        height: requestData.height,
                        nationality: requestData.nationality,
                        work_foot: requestData.work_foot,
                        birthday: requestData.birthday,
                        age: requestData.age,
                        client_owner: requestData.client_owner
                    };
                }
                
                console.log('Created player object to return:', createdPlayer);
                
                onSuccess(createdPlayer);
                handleClose();
                return; // Выход из функции после создания
            } else {
                // Редактирование существующего игрока
                const playerId = player.id || player.player_id;
                
                console.log('Editing player:', playerId);
                console.log('Request data (without image):', {...requestData, image: requestData.image ? `[base64 string, length: ${requestData.image.length}]` : 'not included'});
                console.log('Has new image:', !!imageBase64);
                
                // Дополнительная проверка перед отправкой
                if (requestData.image && typeof requestData.image === 'string' && requestData.image.length < 1000) {
                    throw new Error(`КРИТИЧЕСКАЯ ОШИБКА: Попытка отправить слишком маленькое изображение (${requestData.image.length} символов). Отправка отменена для предотвращения повреждения данных.`);
                }
                
                response = await axios.post(
                    BASE_URL.url + `/players/change/${playerId}`,
                    requestData,
                    {
                        headers: {
                            "Authorization": "Bearer " + tokens.access_token,
                            'Content-Type': 'application/json',
                        }
                    }
                );
                
                console.log('Server response after editing:', response.data);
                
                alert('Данные игрока успешно обновлены!');
                
                // Получаем путь к изображению с сервера
                let imagePath = response.data?.image || player.image;
                
                // Если сервер вернул путь, добавляем timestamp для обхода кэша
                if (imagePath && imagePath.startsWith('/')) {
                    // Убираем старый timestamp если есть
                    imagePath = imagePath.split('?')[0];
                    // Добавляем новый timestamp
                    imagePath = `${imagePath}?v=${Date.now()}`;
                }
                
                // Формируем обновленные данные игрока
                const updatedPlayer = {
                    ...player,
                    ...formData,
                    id: playerId,
                    player_id: playerId,
                    position_id: requestData.position_id,
                    weight: requestData.weight,
                    height: requestData.height,
                    work_foot: requestData.work_foot,
                    age: requestData.age,
                    image: imagePath
                };
                
                console.log('Updated player object:', updatedPlayer);
                
                onSuccess(updatedPlayer);
                handleClose();
                return;
            }

            onSuccess(response.data);
            handleClose();
        } catch (error) {
            console.error('Error saving player:', error);
            let errorMessage = isCreateMode 
                ? 'Произошла ошибка при создании игрока' 
                : 'Произошла ошибка при обновлении данных игрока';
            
            if (error.response) {
                errorMessage = error.response?.data?.message 
                    || error.response?.data?.error 
                    || JSON.stringify(error.response?.data)
                    || `Ошибка сервера: ${error.response.status}`;
            }
            
            alert('Ошибка:\n' + errorMessage);
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        setFormData({
            family: '',
            name: '',
            father_name: '',
            birthday: '',
            position: 'Нападающий',
            nationality: 'Россия',
            height: '',
            weight: '',
            working_foot: 'Правая',
            image: ''
        });
        setPhotoPreview(null);
        setPhotoFile(null);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <>
            {showCropper && (
                <ImageCropper
                    image={imageToCrop}
                    onCropComplete={handleCropComplete}
                    onCancel={handleCropCancel}
                />
            )}
            
            <div className="modalOverlay">
                <div className="editPlayerModalContent">
                    <div className="editPlayerModalHeader">
                    <h2>{onDraftSave ? 'Карточка нового игрока' : isCreateMode ? 'Создать нового игрока' : 'Редактировать данные игрока'}</h2>
                    <button className="closeButton" onClick={handleClose}>
                        <FiX />
                    </button>
                </div>

                <div className="editPlayerModalBody">
                    <div className="formGrid">
                        {/* Фото игрока */}
                        <div className="photoSection">
                            <label>Фото игрока</label>
                            <div className="photoUploadWrapper">
                                {photoPreview ? (
                                    <div className="photoPreview">
                                        <img src={photoPreview} alt="Preview" />
                                    </div>
                                ) : (
                                    <div className="noPhotoPreview">Нет фото</div>
                                )}
                                <label className="uploadButton">
                                    <FiUpload /> Загрузить фото
                                    <input 
                                        type="file" 
                                        accept="image/*" 
                                        onChange={handlePhotoChange}
                                        style={{display: 'none'}}
                                    />
                                </label>
                            </div>
                        </div>

                        {/* Основная информация */}
                        <div className="formSection">
                            <div className="formField required">
                                <label>Фамилия *</label>
                                <input
                                    type="text"
                                    value={formData.family}
                                    onChange={(e) => handleInputChange('family', e.target.value)}
                                    placeholder="Введите фамилию"
                                    disabled={isSaving}
                                />
                            </div>

                            <div className="formField required">
                                <label>Имя *</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                    placeholder="Введите имя"
                                    disabled={isSaving}
                                />
                            </div>

                            <div className="formField">
                                <label>Отчество</label>
                                <input
                                    type="text"
                                    value={formData.father_name}
                                    onChange={(e) => handleInputChange('father_name', e.target.value)}
                                    placeholder="Введите отчество"
                                    disabled={isSaving}
                                />
                            </div>

                            <div className="formField required">
                                <label>Дата рождения *</label>
                                <input
                                    type="date"
                                    value={formData.birthday}
                                    onChange={(e) => handleInputChange('birthday', e.target.value)}
                                    required
                                    disabled={isSaving}
                                />
                            </div>

                            <div className="formField">
                                <label>Позиция</label>
                                <select
                                    value={formData.position}
                                    onChange={(e) => handleInputChange('position', e.target.value)}
                                    disabled={isSaving}
                                >
                                    {POSITIONS.map(pos => (
                                        <option key={pos.value} value={pos.value}>
                                            {pos.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="formField">
                                <label>Национальность</label>
                                <input
                                    type="text"
                                    value={formData.nationality}
                                    onChange={(e) => handleInputChange('nationality', e.target.value)}
                                    placeholder="Введите национальность"
                                    disabled={isSaving}
                                />
                            </div>

                            <div className="formRow">
                                <div className="formField">
                                    <label>Рост (см)</label>
                                    <input
                                        type="number"
                                        value={formData.height}
                                        onChange={(e) => handleInputChange('height', e.target.value)}
                                        placeholder="180"
                                        min="0"
                                        max="250"
                                        disabled={isSaving}
                                    />
                                </div>

                                <div className="formField">
                                    <label>Вес (кг)</label>
                                    <input
                                        type="number"
                                        value={formData.weight}
                                        onChange={(e) => handleInputChange('weight', e.target.value)}
                                        placeholder="75"
                                        min="0"
                                        max="200"
                                        disabled={isSaving}
                                    />
                                </div>
                            </div>

                            <div className="formField">
                                <label>Рабочая нога</label>
                                <select
                                    value={formData.working_foot}
                                    onChange={(e) => handleInputChange('working_foot', e.target.value)}
                                    disabled={isSaving}
                                >
                                    {WORKING_FOOT.map(foot => (
                                        <option key={foot.value} value={foot.value}>
                                            {foot.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="modalActions">
                        <button 
                            className="cancelButton" 
                            onClick={handleClose}
                            disabled={isSaving}
                        >
                            Отмена
                        </button>
                        <button 
                            className="saveButton" 
                            onClick={handleSave}
                            disabled={isSaving || !formData.family || !formData.name || !formData.birthday}
                        >
                            {isSaving ? 'Сохранение...' : (onDraftSave ? 'Готово' : isCreateMode ? 'Создать игрока' : 'Сохранить изменения')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
}
